// Admin Account Merge — Pick → Review → Confirm, on one page.
// File: frontend/src/pages/AdminMergePage.js
//
// Consumes POST /api/admin/merge/plan and /apply
// (backend/app/routers/admin_merge.py).
//
// WHAT THE REVIEW TABLE IS. It is not a summary of decisions the server
// already made — it is the mechanism. Measured against the owner's real
// duplicate: of 198 profile columns, 160 are identical on both sides, 21 are
// present on one side only, and only 4 genuinely differ. `plan()` already
// drops the identical ones (account_merge.py:400), so what arrives here is
// the 38 that are not, and this page's job is to push the handful actually in
// contention to the top and get the other 21 out of the way.
//
// THREE RULES THIS FILE EXISTS TO KEEP:
//
// 1. `overrides` carries only what the admin clicked. A destructive entry
//    renders with NOTHING selected — the server's suggested winner is shown
//    as text beside the radios, never as a checked control — so an untouched
//    page cannot POST a decision nobody made. That is the browser half of the
//    invariant admin_merge.py's module docstring describes; the server refuses
//    to delete a row whose column is absent from `overrides`, and this makes
//    sure absence means "not decided" rather than "not rendered".
// 2. A 409 is a state, not an error. It means the plan went stale or a
//    destructive row has no decision, and the answer is a fresh plan on
//    screen, not a red toast. See `applyMerge`.
// 3. The credential move is spelled out in full before it can be clicked. It
//    is the one part of a merge that a reversal cannot fully undo — the
//    survivor's old password is gone and the family needs a reset — so the
//    consequence is on screen in plain words, not in a tooltip.
import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AdminPage from '../components/AdminShell';
import './AdminMergePage.css';

// The five `FieldPlan.reason` values (account_merge.resolve), in plain
// language. An unknown reason falls through to the raw string rather than to
// a blank chip — a new server rule should look unfamiliar, not invisible.
const REASON_LABEL = {
  'verified-wins': 'Verified',
  'fill-empty': 'Only value',
  'photo-fill-only': 'Photo kept',
  'guardian-survivor': 'Family entered',
  'survivor-default': 'Default',
  // High school XOR college: school_level picks the side, the school columns follow.
  'school-level-conflict': 'High school or college',
  'school-level-named-side': 'Only school',
  'follows-school-level': 'Follows school level',
};

// Amber reads as "look at this", green as "settled". Only `survivor-default`
// is a genuine coin-toss, so it is the one that gets the attention colour.
const REASON_TONE = {
  'verified-wins': 'adm-chip--green',
  'fill-empty': '',
  'photo-fill-only': '',
  'guardian-survivor': '',
  'survivor-default': 'adm-chip--amber',
  'school-level-conflict': 'adm-chip--amber',
  'school-level-named-side': '',
  'follows-school-level': '',
};

// account_merge.ROW_ID_SEP — a plan entry whose column contains ':' is a
// row-level collision in one of the nine verification-carrying tables
// ("athlete_stat_values:forty_yard_dash"), not an athlete_profiles column.
const ROW_ID_SEP = ':';
const isRowEntry = (column) => column.includes(ROW_ID_SEP);

// Mirrors account_merge._empty closely enough to bucket a row for display.
// It decides which side of the "show unchanged" toggle a field falls on and
// nothing else — the server has already decided every winner, so a
// disagreement here mis-files a row, it does not mis-merge one.
function isEmpty(v) {
  if (v === null || v === undefined) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === 'object') return Object.keys(v).length === 0;
  return false;
}

// A MergeResult.collisions_resolved key is written by
// account_merge._resolve_collisions as "<schema>.<table> [<index name>]".
// Show the table; the raw key stays visible underneath so the index that
// caused it is never hidden.
function collisionTable(key) {
  return key.replace(/\s*\[.*\]\s*$/, '').replace(/^public\./, '');
}

function fmtDate(iso) {
  if (!iso) return null;
  const d = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// A row-collision entry's value is the whole row payload (a dict), not one
// column — account_merge.row_collisions passes the payload to `resolve` on
// purpose, so a NULL in a nominated column cannot delete a verified row whose
// real data lives elsewhere in the same record. Render it the same way: every
// populated key, so what is about to be deleted is legible.
function ValueCell({ value, verified }) {
  const mark = verified ? (
    <span className="mrg-verified" title="Verified" aria-label="Verified">✓</span>
  ) : null;

  if (isEmpty(value)) {
    return <span className="mrg-val mrg-val--empty">empty{mark}</span>;
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    const entries = Object.entries(value).filter(([, v]) => !isEmpty(v));
    return (
      <span className="mrg-val mrg-val--payload">
        <span className="mrg-payload">
          {entries.map(([k, v]) => (
            <span key={k} className="mrg-payload__pair">
              <span className="mrg-payload__key">{k}</span>
              {String(v)}
            </span>
          ))}
        </span>
        {mark}
      </span>
    );
  }
  const text = Array.isArray(value) ? value.join(', ') : String(value);
  return <span className="mrg-val">{text}{mark}</span>;
}

function AccountCard({ label, account, userId, tone }) {
  const email = account?.email || (userId != null ? `User #${userId}` : null);
  if (!email) return null;
  const name = account && (account.first_name || account.last_name)
    ? `${account.first_name || ''} ${account.last_name || ''}`.trim()
    : null;
  const created = fmtDate(account?.created_at);
  const last = fmtDate(account?.last_login_at);
  const count = account?.login_count || 0;
  return (
    <div className={`mrg-card mrg-card--${tone}`}>
      <div className="mrg-card__role">{label}</div>
      <div className="mrg-card__email">{email}</div>
      {name && <div className="mrg-card__name">{name}</div>}
      {account ? (
        <dl className="mrg-card__meta">
          <div><dt>Registered</dt><dd>{created || 'unknown'}</dd></div>
          <div>
            <dt>Last sign-in</dt>
            <dd>{last || 'Never signed in'}</dd>
          </div>
          <div>
            <dt>Sign-ins</dt>
            <dd>{count === 1 ? '1 sign-in' : `${count} sign-ins`}</dd>
          </div>
        </dl>
      ) : (
        <p className="adm-note">
          Account details could not be loaded for this id. The merge still runs
          against user {userId}; the plan below is what will happen.
        </p>
      )}
    </div>
  );
}

// The possible-duplicate groups behind the Admin home's "Possible duplicates" count (same name, plus
// the same phone or the same high school). The server lists the suggested survivor first: an account
// that has signed in, then the oldest. Picking fills both sides below.
//
// "Not a duplicate" (David, 2026-11-05) is for a group that is really an athlete and a guardian with a
// similar name or email: the detection rules stay as they are, the server just stops listing that set
// of accounts (and stops counting it on Admin home). Undo puts it back.
const groupKey = (g) => g.members.map((m) => m.id).join('-');

function Candidates({ API_URL, onPick, donorId, survivorId }) {
  const [groups, setGroups] = useState(null);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(null);
  const [actionError, setActionError] = useState('');
  // The last dismissed group and where it sat, so Undo can put it back in place.
  const [dismissed, setDismissed] = useState(null);

  const dismiss = (g, index) => {
    const ids = g.members.map((m) => m.id);
    setBusy(groupKey(g)); setActionError('');
    axios.post(`${API_URL}/admin/merge/candidates/dismiss`, { user_ids: ids })
      .then(() => {
        setGroups((cur) => cur.filter((x) => groupKey(x) !== groupKey(g)));
        setDismissed({ group: g, index });
      })
      .catch(() => setActionError(`Could not dismiss ${g.name}.`))
      .finally(() => setBusy(null));
  };
  const undo = () => {
    const { group, index } = dismissed;
    setActionError('');
    axios.post(`${API_URL}/admin/merge/candidates/undismiss`, { user_ids: group.members.map((m) => m.id) })
      .then(() => {
        setGroups((cur) => [...cur.slice(0, index), group, ...cur.slice(index)]);
        setDismissed(null);
      })
      .catch(() => setActionError(`Could not undo dismissing ${group.name}.`));
  };
  const undoBar = dismissed && (
    <p className="adm-note mrg-cand__undo" data-testid="merge_candidates_undo">
      {dismissed.group.name} marked not a duplicate.{' '}
      <button type="button" className="adm-link" onClick={undo}>Undo</button>
    </p>
  );
  useEffect(() => {
    axios.get(`${API_URL}/admin/merge/candidates`)
      .then((r) => setGroups(r.data?.groups || []))
      .catch(() => setError(true));
  }, [API_URL]);
  if (error) return <p className="adm-error">Could not load the possible duplicates.</p>;
  if (!groups) return <div className="adm-loading">Finding possible duplicates…</div>;
  if (!groups.length) return <>{undoBar}<p className="adm-note">No possible duplicates right now.</p></>;
  return (
    <>
    {undoBar}
    {actionError && <p className="adm-error">{actionError}</p>}
    <ul className="mrg-cands" data-testid="merge_candidates">
      {groups.map((g, index) => {
        const [keep, ...rest] = g.members;
        return (
          <li key={groupKey(g)} className="mrg-cand">
            <div className="mrg-cand__head">
              <strong>{g.name}</strong>
              <span className="adm-chip adm-chip--amber">{g.size} accounts</span>
              {(g.match || []).map((m) => (
                <span key={m} className="adm-chip">{m === 'school' ? 'Same school' : 'Same phone'}</span>
              ))}
            </div>
            {g.members.map((m) => {
              const role = m.id === donorId ? 'donor' : m.id === survivorId ? 'survivor' : null;
              return (
                <div key={m.id} className={`mrg-cand__row${role ? ` mrg-cand__row--${role}` : ''}`}>
                  <span className="mrg-cand__email">{m.email || `User #${m.id}`}</span>
                  <span className="adm-cell__meta">
                    #{m.id} · joined {fmtDate(m.created_at) || 'unknown'} · {m.login_count === 1 ? '1 sign-in' : `${m.login_count} sign-ins`}
                    {m.phone ? ` · ${m.phone}` : ' · no phone'}
                    {m.high_school ? ` · ${m.high_school}` : ''}
                  </span>
                  <span className="mrg-cand__acts">
                    <button type="button" className="adm-btn adm-btn--sm" onClick={() => onPick('survivor', m)}>Keep</button>
                    <button type="button" className="adm-btn adm-btn--sm" onClick={() => onPick('donor', m)}>Merge in</button>
                  </span>
                </div>
              );
            })}
            <div className="mrg-cand__foot">
              {rest.length === 1 && (
                <button type="button" className="adm-btn adm-btn--primary adm-btn--sm"
                  onClick={() => { onPick('survivor', keep); onPick('donor', rest[0]); }}>
                  Review this pair
                </button>
              )}
              <button type="button" className="adm-btn adm-btn--sm" disabled={busy === groupKey(g)}
                onClick={() => dismiss(g, index)} data-testid={`merge_candidate_dismiss_${groupKey(g)}`}
                title="Not the same person (e.g. an athlete and their guardian). Removes this group from the list; nothing is merged or changed.">
                {busy === groupKey(g) ? 'Dismissing…' : 'Not a duplicate'}
              </button>
            </div>
          </li>
        );
      })}
    </ul>
    </>
  );
}

function AdminMergeFlow({ initialDonorId = null, initialSurvivorId = null }) {
  const { API_URL } = useAuth();

  const [step, setStep] = useState(initialDonorId && initialSurvivorId ? 'review' : 'pick');
  const [donorId, setDonorId] = useState(initialDonorId);
  const [survivorId, setSurvivorId] = useState(initialSurvivorId);
  const [donorAccount, setDonorAccount] = useState(null);
  const [survivorAccount, setSurvivorAccount] = useState(null);
  const [donorQuery, setDonorQuery] = useState('');
  const [survivorQuery, setSurvivorQuery] = useState('');
  const [lookupError, setLookupError] = useState({});

  const [plan, setPlan] = useState(null);
  const [planError, setPlanError] = useState(null);
  const [loading, setLoading] = useState(false);

  // The ONLY decisions that reach the server. Seeded empty and written only
  // by a click. Cleared whenever the two accounts change, because the keys
  // are column names and a swap reverses what "donor" means.
  const [overrides, setOverrides] = useState({});
  const [showUnchanged, setShowUnchanged] = useState(false);
  const [transplant, setTransplant] = useState(false);
  const [notice, setNotice] = useState(null);          // 409 message, re-shown on Review
  const [flagged, setFlagged] = useState([]);          // unconfirmed_columns from a 409
  const [result, setResult] = useState(null);
  const [applyError, setApplyError] = useState(null);
  const [applying, setApplying] = useState(false);

  const fetchPlan = useCallback(async (d, s) => {
    setLoading(true);
    setPlanError(null);
    try {
      const res = await axios.post(`${API_URL}/admin/merge/plan`, {
        donor_id: d, survivor_id: s,
      });
      setPlan(res.data);
      return res.data;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setPlan(null);
      setPlanError(typeof detail === 'string' ? detail
        : 'The merge plan could not be built for these two accounts.');
      return null;
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    if (donorId && survivorId) {
      setOverrides({});
      fetchPlan(donorId, survivorId);
    }
  }, [donorId, survivorId, fetchPlan]);

  // /api/admin/users searches email and name only — there is no id filter and
  // no GET /admin/users/{id}. A numeric entry is therefore matched against
  // the ids in whatever the search returns, and when that misses we take the
  // id at face value and say on the card that the details did not load,
  // rather than blocking a merge the admin can name precisely.
  const lookup = async (side) => {
    const q = (side === 'donor' ? donorQuery : survivorQuery).trim();
    const setAccount = side === 'donor' ? setDonorAccount : setSurvivorAccount;
    const setId = side === 'donor' ? setDonorId : setSurvivorId;
    if (!q) return;
    setLookupError((p) => ({ ...p, [side]: null }));
    try {
      const res = await axios.get(`${API_URL}/admin/users`, {
        params: { search: q, limit: 25 },
      });
      const items = Array.isArray(res.data) ? res.data : (res.data?.items || []);
      const numeric = /^\d+$/.test(q) ? Number(q) : null;
      const hit = items.find((u) => (numeric != null
        ? u.id === numeric
        : (u.email || '').toLowerCase() === q.toLowerCase()))
        || (items.length === 1 ? items[0] : null);
      if (hit) {
        setAccount(hit);
        setId(hit.id);
        return;
      }
      if (numeric != null) {
        setAccount(null);
        setId(numeric);
        return;
      }
      setAccount(null);
      setId(null);
      setLookupError((p) => ({
        ...p,
        [side]: items.length
          ? `${items.length} accounts matched "${q}". Enter the full email address or the user id.`
          : `No account matched "${q}".`,
      }));
    } catch (e) {
      setLookupError((p) => ({ ...p, [side]: 'Account lookup failed.' }));
    }
  };

  const pickCandidate = (side, m) => {
    (side === 'donor' ? setDonorAccount : setSurvivorAccount)(m);
    (side === 'donor' ? setDonorId : setSurvivorId)(m.id);
    (side === 'donor' ? setDonorQuery : setSurvivorQuery)(m.email || String(m.id));
    setLookupError((p) => ({ ...p, [side]: null }));
  };

  const swap = () => {
    setOverrides({});
    // Same reason `overrides` is cleared: "move the donor's login onto the
    // survivor" names the two sides, so after a flip a checked box means the
    // opposite of what the admin ticked. Make them tick it again.
    setTransplant(false);
    setDonorAccount(survivorAccount);
    setSurvivorAccount(donorAccount);
    setDonorId(survivorId);
    setSurvivorId(donorId);
  };

  const fields = plan?.fields || [];
  // A field is in contention when both sides hold a value, or when settling
  // it deletes a row. Everything else — the 21 one-sided columns in the
  // owner's real duplicate — has exactly one possible answer and goes behind
  // the toggle. Identical columns never arrive: plan() drops them server-side.
  const contested = fields.filter(
    (f) => f.destructive || (!isEmpty(f.donor_value) && !isEmpty(f.survivor_value)));
  const unchanged = fields.filter((f) => !contested.includes(f));
  const destructive = fields.filter((f) => f.destructive);
  const undecided = destructive.filter((f) => !(f.column in overrides));
  const canContinue = plan != null && undecided.length === 0;

  const decide = (column, side) =>
    setOverrides((prev) => ({ ...prev, [column]: side }));

  const donorLabel = donorAccount?.email || (donorId != null ? `User #${donorId}` : 'the donor');
  const survivorLabel = survivorAccount?.email
    || (survivorId != null ? `User #${survivorId}` : 'the survivor');

  const applyMerge = async () => {
    setApplying(true);
    setApplyError(null);
    try {
      const res = await axios.post(`${API_URL}/admin/merge/apply`, {
        donor_id: donorId,
        survivor_id: survivorId,
        plan_hash: plan.plan_hash,
        overrides,
        transplant_credentials: transplant,
      });
      setResult(res.data);
      setStep('done');
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409) {
        // A 409 is a state, not an error — but it has TWO causes and they need
        // different landings, and the status alone cannot tell them apart:
        //
        //   * an unconfirmed destructive row (structured detail, carries
        //     `unconfirmed_columns`) — the decision is on the Review table;
        //   * every ValueError apply() raises, surfaced verbatim as a plain
        //     string: a stale `plan_hash`, the login-lockout guard, the
        //     credential-lockout guard, an already-merged account.
        //
        // Only the stale hash is answered by re-reading the plan. The lockout
        // refusals are answered by the transplant checkbox, which lives on
        // THIS step — bouncing to Review would move the admin away from the
        // one control that resolves the message they were just shown.
        //
        // So: always re-fetch, because a fresh plan is never wrong to hold,
        // then let the HASH decide where to land. A changed hash means the
        // preview really was stale.
        const cols = Array.isArray(detail?.unconfirmed_columns) ? detail.unconfirmed_columns : [];
        const message = typeof detail === 'string' ? detail
          : (detail?.message || 'These accounts changed since the preview. Review again.');
        const before = plan.plan_hash;
        if (cols.length) {
          // Never leave a decision the server rejected sitting in `overrides`
          // looking settled — it has to be made again.
          setOverrides((prev) => {
            const next = { ...prev };
            cols.forEach((c) => { delete next[c]; });
            return next;
          });
        }
        const fresh = await fetchPlan(donorId, survivorId);
        const stale = fresh != null && fresh.plan_hash !== before;
        if (cols.length || stale) {
          setNotice(message);
          setFlagged(cols);
          setStep('review');
        } else {
          setApplyError(message);
        }
      } else {
        setApplyError(typeof detail === 'string' ? detail
          : 'The merge did not run. Nothing was changed.');
      }
    } finally {
      setApplying(false);
    }
  };

  const renderRow = (f) => {
    // A school column that follows school_level shows school_level's side and
    // cannot be picked on its own: the server takes it from school_level.
    const follows = f.reason === 'follows-school-level';
    const levelEntry = follows ? fields.find((x) => x.column === 'school_level') : null;
    const chosen = follows
      ? (overrides.school_level || (levelEntry && !levelEntry.destructive ? levelEntry.winner : undefined))
      : overrides[f.column];
    // A destructive row is rendered UNDECIDED. The suggestion is text, never
    // a checked control — see rule 1 at the top of this file.
    const donorChecked = (f.destructive || follows) ? chosen === 'donor' : (chosen || f.winner) === 'donor';
    const survChecked = (f.destructive || follows) ? chosen === 'survivor' : (chosen || f.winner) === 'survivor';
    const needs = f.destructive && !chosen;
    const isFlagged = flagged.includes(f.column);
    const name = `merge-${f.column}`;
    return (
      <tr
        key={f.column}
        className={[
          f.destructive ? 'mrg-row--destructive' : '',
          needs ? 'mrg-row--needs' : '',
          isFlagged ? 'mrg-row--flagged' : '',
        ].filter(Boolean).join(' ')}
      >
        <td>
          <span className="mrg-col">{f.column}</span>
          {isRowEntry(f.column) && (
            <span className="mrg-col__sub">
              row in {f.column.split(ROW_ID_SEP)[0]}
            </span>
          )}
          {f.destructive && (
            <span className="mrg-col__sub mrg-col__sub--warn">
              {f.reason === 'school-level-conflict'
                ? "settling this clears the other side's school"
                : 'settling this deletes the losing row'}
            </span>
          )}
        </td>
        <td className={donorChecked ? 'mrg-td--win' : ''}>
          <label className="mrg-pick">
            <input
              type="radio"
              name={name}
              checked={donorChecked}
              disabled={follows}
              onChange={() => decide(f.column, 'donor')}
              aria-label={`Keep the donor value for ${f.column}`}
            />
            <ValueCell value={f.donor_value} verified={!!f.donor_verified} />
          </label>
        </td>
        <td className={survChecked ? 'mrg-td--win' : ''}>
          <label className="mrg-pick">
            <input
              type="radio"
              name={name}
              checked={survChecked}
              disabled={follows}
              onChange={() => decide(f.column, 'survivor')}
              aria-label={`Keep the surviving account's value for ${f.column}`}
            />
            <ValueCell value={f.survivor_value} verified={!!f.survivor_verified} />
          </label>
        </td>
        <td className="adm-td--right">
          <span className={`adm-chip ${REASON_TONE[f.reason] || ''}`}>
            {REASON_LABEL[f.reason] || f.reason}
          </span>
          {needs && (
            <span className="mrg-needs">
              Needs your decision
              <span className="mrg-needs__hint">suggested: {f.winner}</span>
            </span>
          )}
        </td>
      </tr>
    );
  };

  const table = (rows) => (
    <div className="adm-tablewrap">
      <table className="adm-table mrg-table">
        <thead>
          <tr>
            <th>Field</th>
            <th>Duplicate ({donorLabel})</th>
            <th>Keeping ({survivorLabel})</th>
            <th className="adm-th--right">Why</th>
          </tr>
        </thead>
        <tbody>{rows.map(renderRow)}</tbody>
      </table>
    </div>
  );

  return (
    <AdminPage
      active="merge"
      title="Merge Accounts"
      subtitle="Fold a duplicate account into the one you are keeping. Nothing is written until the last step."
    >
      <ol className="mrg-steps" aria-label="Merge steps">
        {['Pick', 'Review', 'Confirm'].map((s, i) => {
          const order = { pick: 0, review: 1, confirm: 2, done: 3 }[step];
          return (
            <li
              key={s}
              className={`mrg-step ${order === i ? 'mrg-step--active' : ''} ${order > i ? 'mrg-step--done' : ''}`}
            >
              <span className="mrg-step__num">{i + 1}</span>{s}
            </li>
          );
        })}
      </ol>

      {/* ------------------------------------------------------------ Pick */}
      {step === 'pick' && (
        <div className="adm__card">
          <div className="adm-card__head">
            <h2 className="adm-subhead">Possible duplicates</h2>
          </div>
          <p className="adm-card__sub">
            Athletes who share a phone number and a name. Pick a pair to fill in the form below.
          </p>
          <Candidates API_URL={API_URL} onPick={pickCandidate} donorId={donorId} survivorId={survivorId} />
        </div>
      )}
      {step === 'pick' && (
        <div className="adm__card">
          <div className="adm-card__head">
            <h2 className="adm-subhead">Which two accounts?</h2>
          </div>
          <p className="adm-card__sub">
            Enter an email address or a user id for each side. The duplicate is
            the account that gets folded in and deactivated.
          </p>
          <div className="mrg-pickgrid">
            {[
              { side: 'donor', label: 'Duplicate to merge in (donor)', q: donorQuery, set: setDonorQuery },
              { side: 'survivor', label: 'Account to keep (survivor)', q: survivorQuery, set: setSurvivorQuery },
            ].map(({ side, label, q, set }) => (
              <div className="adm-field" key={side}>
                <label className="adm-field__label" htmlFor={`mrg-${side}`}>{label}</label>
                <div className="mrg-lookup">
                  <input
                    id={`mrg-${side}`}
                    className="adm-input"
                    value={q}
                    placeholder="email@example.com or 1234"
                    onChange={(e) => set(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') lookup(side); }}
                  />
                  <button type="button" className="adm-btn" onClick={() => lookup(side)}>Find</button>
                </div>
                {lookupError[side] && <p className="adm-error">{lookupError[side]}</p>}
              </div>
            ))}
          </div>

          {(donorId || survivorId) && (
            <div className="mrg-cards">
              <AccountCard label="Duplicate — will be deactivated" tone="donor"
                account={donorAccount} userId={donorId} />
              <button type="button" className="adm-btn adm-btn--sm mrg-swap"
                onClick={swap} disabled={!donorId || !survivorId}>
                ⇄ Swap
              </button>
              <AccountCard label="Keeping — survives the merge" tone="survivor"
                account={survivorAccount} userId={survivorId} />
            </div>
          )}

          {plan && plan.suggested_survivor_id !== survivorId && (
            <p className="adm-note adm-note--warn">
              The older, more-used account here is user {plan.suggested_survivor_id}.
              Consider swapping so that one survives.
            </p>
          )}
          {planError && <p className="adm-error">{planError}</p>}

          <div className="mrg-actions">
            <button
              type="button"
              className="adm-btn adm-btn--primary"
              disabled={!plan || loading}
              onClick={() => setStep('review')}
            >
              Continue to review
            </button>
          </div>
        </div>
      )}

      {/* ---------------------------------------------------------- Review */}
      {step === 'review' && (
        <>
          {notice && <div className="adm__card adm__card--compact mrg-notice">{notice}</div>}
          {loading && <div className="adm-loading">Building the plan…</div>}
          {planError && <p className="adm-error">{planError}</p>}
          {plan && (
            <>
              <div className="mrg-cards mrg-cards--tight">
                <AccountCard label="Duplicate — will be deactivated" tone="donor"
                  account={donorAccount} userId={donorId} />
                <button type="button" className="adm-btn adm-btn--sm mrg-swap" onClick={swap}>
                  ⇄ Swap
                </button>
                <AccountCard label="Keeping — survives the merge" tone="survivor"
                  account={survivorAccount} userId={survivorId} />
              </div>

              <div className="adm__card adm__card--flush">
                <div className="adm-card__head">
                  <h2 className="adm-subhead">
                    {contested.length === 0
                      ? 'Nothing is in contention'
                      : `${contested.length} field${contested.length === 1 ? '' : 's'} to settle`}
                  </h2>
                  <p className="adm-card__sub">
                    Pick the value you want on the surviving account. Identical
                    fields are not listed at all — the server leaves them out of
                    the plan.
                  </p>
                </div>
                {contested.length > 0
                  ? table(contested)
                  : <div className="adm-empty">Every field either matches or has only one value.</div>}
              </div>

              {unchanged.length > 0 && (
                <div className="adm__card adm__card--flush">
                  <div className="adm-card__head">
                    <button
                      type="button"
                      className="adm-btn adm-btn--sm"
                      onClick={() => setShowUnchanged((v) => !v)}
                    >
                      {showUnchanged
                        ? 'Hide unchanged fields'
                        : `Show ${unchanged.length} unchanged field${unchanged.length === 1 ? '' : 's'}`}
                    </button>
                    <p className="adm-card__sub">
                      These have a value on one side only, so there is nothing to
                      choose — the value that exists is the one that is kept.
                    </p>
                  </div>
                  {showUnchanged && table(unchanged)}
                </div>
              )}

              <div className="mrg-actions">
                {undecided.length > 0 && (
                  <p className="adm-note adm-note--warn">
                    {undecided.length} row{undecided.length === 1 ? '' : 's'} above
                    would be deleted. Choose a side for each before continuing.
                  </p>
                )}
                <button type="button" className="adm-btn" onClick={() => setStep('pick')}>
                  Back
                </button>
                <button
                  type="button"
                  className="adm-btn adm-btn--primary"
                  disabled={!canContinue}
                  onClick={() => { setNotice(null); setFlagged([]); setStep('confirm'); }}
                >
                  Continue to confirm
                </button>
              </div>
            </>
          )}
        </>
      )}

      {/* --------------------------------------------------------- Confirm */}
      {step === 'confirm' && plan && (
        <div className="adm__card">
          <div className="adm-card__head">
            <h2 className="adm-subhead">Confirm the merge</h2>
          </div>
          <p className="mrg-sentence">
            {donorLabel} will be merged into {survivorLabel} and deactivated.
            This can be reversed.
          </p>

          <h3 className="adm-subhead mrg-sub">What this writes</h3>
          <table className="adm-table mrg-summary">
            <tbody>
              <tr>
                <td>Profile fields taken from the duplicate</td>
                <td className="adm-td--num">
                  {fields.filter((f) => !isRowEntry(f.column)
                    && (overrides[f.column] || f.winner) === 'donor').length}
                </td>
              </tr>
              <tr>
                <td>
                  Row conflicts you settled
                  <span className="mrg-col__sub">
                    each one deletes the losing row — the only deletions you decide
                  </span>
                </td>
                <td className="adm-td--num">{destructive.length}</td>
              </tr>
            </tbody>
          </table>
          <p className="adm-note">
            Athlete data on the duplicate — stats, videos, accolades, messages —
            is re-parented to the surviving account. Three further deletions the
            merge makes on its own, all counted for you on the next screen: the
            duplicate&apos;s own profile row, once the fields above have been
            copied off it; rows that clash on a database uniqueness rule, where
            the duplicate&apos;s copy is dropped; and the derived daily
            completeness snapshot, the one table where the surviving
            account&apos;s row is dropped instead because the duplicate&apos;s is
            authoritative. Those counts are not known until the merge runs.
          </p>

          <h3 className="adm-subhead mrg-sub">Login</h3>
          <label className="mrg-check">
            <input
              type="checkbox"
              checked={transplant}
              onChange={(e) => setTransplant(e.target.checked)}
            />
            <span>Move the donor&apos;s login onto the surviving account</span>
          </label>
          {transplant ? (
            <div className="mrg-danger">
              <p>
                The family will sign in as <strong>{donorLabel}</strong> from now
                on, with the password they already use for that account.
              </p>
              <p>
                The duplicate loses its Apple and Google sign-in bindings — they
                move with the login.
              </p>
              <p>
                <strong>
                  The surviving account&apos;s previous password cannot be
                  recovered afterward.
                </strong>{' '}
                Undoing this means sending that person a password reset.
              </p>
            </div>
          ) : (
            <p className="adm-note">
              Left unchecked, the surviving account keeps its own email and
              password. If the duplicate is the only side that has ever signed
              in, the merge will refuse and tell you so.
            </p>
          )}

          {applyError && <p className="adm-error">{applyError}</p>}

          <div className="mrg-actions">
            <button type="button" className="adm-btn" onClick={() => setStep('review')}>
              Back to review
            </button>
            <button
              type="button"
              className="adm-btn adm-btn--dark"
              disabled={applying}
              onClick={applyMerge}
            >
              {applying ? 'Merging…' : 'Merge these accounts'}
            </button>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------ Done */}
      {step === 'done' && result && (
        <div className="adm__card">
          <div className="adm-card__head">
            <h2 className="adm-subhead">Merge complete</h2>
          </div>
          <p className="mrg-sentence">
            {donorLabel} is now merged into {survivorLabel} and deactivated.
          </p>
          <div className="mrg-actions mrg-actions--done">
            {/* A link to this same route gets a new location.key, which remounts the flow (see the
                default export): back to Pick with the duplicates list fetched again. */}
            <Link className="adm-btn adm-btn--primary" to="/admin/merge">Merge another</Link>
            {survivorId && (
              <Link className="adm-btn" to={`/athletes/${survivorId}`}>Open the kept account</Link>
            )}
          </div>
          <h3 className="adm-subhead mrg-sub">Rows moved</h3>
          <table className="adm-table mrg-summary">
            <tbody>
              {Object.entries(result.rows_moved || {}).map(([t, n]) => (
                <tr key={t}>
                  <td>{t}</td>
                  <td className="adm-td--num">{n}</td>
                </tr>
              ))}
              {Object.entries(result.rows_superseded || {}).map(([t, n]) => (
                <tr key={`sup-${t}`}>
                  <td>{t} — deleted as the losing side</td>
                  <td className="adm-td--num">{n}</td>
                </tr>
              ))}
              {/* The deletions nobody clicked a radio for: a unique index the
                  two accounts both had a row under. `MergeResult` carries them
                  precisely so they are visible rather than silent, and the
                  Confirm screen promises they are counted here. */}
              {Object.entries(result.collisions_resolved || {}).map(([k, n]) => (
                <tr key={`col-${k}`}>
                  <td>
                    {collisionTable(k)} — deleted on a uniqueness clash
                    <span className="mrg-col__sub">{k}</span>
                  </td>
                  <td className="adm-td--num">{n}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {Object.keys(result.collisions_resolved || {}).length === 0 && (
            <p className="adm-note">No uniqueness clashes needed resolving.</p>
          )}
          <p className="adm-note">
            {(result.fields_written || []).length} profile field
            {(result.fields_written || []).length === 1 ? '' : 's'} written.
            {result.credentials_transplanted
              ? ` Login moved (${(result.credential_fields || []).join(', ')}).`
              : ' Login left as it was.'}
            {result.audit_log_id != null
              && ` The pre-merge snapshot is in audit log #${result.audit_log_id}, which is what makes this reversible.`}
          </p>
        </div>
      )}
    </AdminPage>
  );
}

// Keyed by location so every visit to /admin/merge starts a fresh merge. Before this, finishing a
// merge left the page on "Merge complete" and clicking Merge in the rail did nothing (same route,
// no remount), so the only way back to Pick was a browser refresh.
export default function AdminMergePage(props) {
  const location = useLocation();
  return <AdminMergeFlow key={location.key} {...props} />;
}
